import {
    Button,
    ButtonGroup,
    Menu,
    MenuItem,
    Popover,
    Position
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { useCurrentScenarioMetadata } from "in_queue/contexts/ScenarioPageContext";
import { useIsScenarioCreationEnabled } from "in_queue/hooks/useIsScenarioCreationEnabled";
import { ScenarioOrigin, ScenarioStatus } from "in_queue/types/scenarioType";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ADMIN_TYPE } from "types/roleType";
import { AdminOnlyTooltip } from "../common/AdminOnlyTooltip";
import { AllScenarioTooltips } from "../common/AllScenarioTooltips";
import ArchiveScenarioAlert from "../ScenarioArchive/ArchiveScenarioAlert";
import { useScenarioBuilderRoutes } from "../ScenarioBuilder/useScenarioBuilderRoutes";
import { ScenarioVisibilityDialog } from "../ScenarioVisibilityDialog";
import DuplicateScenarioAction from "./ScenarioActions/DuplicateScenarioAction";
import SubmitScenarioRunButton from "./SubmitScenarioRunButton";

const ScenarioActions: React.FC = () => {
    const scenario = useCurrentScenarioMetadata();
    const { status } = scenario;
    if (scenario.archived) {
        return <ArchivedScenarioActions />;
    } else if (status === ScenarioStatus.Draft) {
        return <DraftScenarioActions />;
    } else if (
        status === ScenarioStatus.RunRequested ||
        status === ScenarioStatus.RunSubmitted ||
        status === ScenarioStatus.Running ||
        status === ScenarioStatus.RunSucceeded
    ) {
        return <CompletedScenarioActions />;
    } else if (status === ScenarioStatus.RunFailed) {
        return <FailedScenarioActions />;
    }
    return null;
};

const DraftScenarioActions = () => {
    const scenario = useCurrentScenarioMetadata();
    const scenarioCreationEnabled = useIsScenarioCreationEnabled({
        stage: scenario.stage
    });
    const { role } = useUserDataContext();
    const navigate = useNavigate();
    const scenarioBuilderRoutes = useScenarioBuilderRoutes();
    const [isArchiveAlertOpen, setIsArchiveAlertOpen] = useState(false);

    const { status } = scenario;

    if (status !== ScenarioStatus.Draft) {
        return <></>;
    }

    const isNotAdmin = role !== ADMIN_TYPE;

    return (
        <>
            <ArchiveScenarioAlert
                isOpen={isArchiveAlertOpen}
                setIsOpen={setIsArchiveAlertOpen}
                action="archive"
            />
            <AdminOnlyTooltip>
                <ButtonGroup>
                    <Button
                        icon={IconNames.EDIT}
                        text="Edit"
                        onClick={() =>
                            navigate(
                                scenarioBuilderRoutes.editHref(
                                    scenario.scenarioId
                                )
                            )
                        }
                        disabled={isNotAdmin}
                    />

                    <Popover
                        position={Position.BOTTOM_RIGHT}
                        content={
                            <Menu>
                                <AllScenarioTooltips stage={scenario.stage}>
                                    <MenuItem
                                        icon={IconNames.DUPLICATE}
                                        text="Duplicate"
                                        onClick={() =>
                                            navigate(
                                                scenarioBuilderRoutes.duplicateHref(
                                                    scenario.scenarioId
                                                )
                                            )
                                        }
                                        disabled={!scenarioCreationEnabled}
                                    />
                                </AllScenarioTooltips>
                                <MenuItem
                                    icon={IconNames.ARCHIVE}
                                    text="Archive scenario"
                                    onClick={() => {
                                        setIsArchiveAlertOpen(true);
                                    }}
                                />
                            </Menu>
                        }
                    >
                        <Button
                            icon={IconNames.CARET_DOWN}
                            disabled={isNotAdmin}
                        />
                    </Popover>
                </ButtonGroup>
            </AdminOnlyTooltip>

            <SubmitScenarioRunButton scenarioId={scenario.scenarioId} />
        </>
    );
};

const CompletedScenarioActions = () => {
    const scenario = useCurrentScenarioMetadata();
    const [isVisibilityDialogOpen, setIsVisibilityDialogOpen] = useState(false);
    const [isArchiveAlertOpen, setIsArchiveAlertOpen] = useState(false);

    if (scenario.origin === ScenarioOrigin.NiraPublished) {
        return <PublishedScenarioActions />;
    }
    if (scenario.type === "SHARED") {
        return <></>;
    }

    return (
        <ButtonGroup>
            <DuplicateScenarioAction />
            <Popover
                position={Position.BOTTOM_RIGHT}
                content={
                    <Menu>
                        <MenuItem
                            icon={IconNames.EYE_OPEN}
                            text="Edit visibility"
                            onClick={() => {
                                setIsVisibilityDialogOpen(true);
                            }}
                        />
                        <MenuItem
                            icon={IconNames.ARCHIVE}
                            text="Archive scenario"
                            onClick={() => {
                                setIsArchiveAlertOpen(true);
                            }}
                        />
                    </Menu>
                }
            >
                <Button icon={IconNames.CARET_DOWN} />
            </Popover>
            <ScenarioVisibilityDialog
                isOpen={isVisibilityDialogOpen}
                onClose={() => setIsVisibilityDialogOpen(false)}
                scenario={scenario}
            />
            <ArchiveScenarioAlert
                isOpen={isArchiveAlertOpen}
                setIsOpen={setIsArchiveAlertOpen}
                action="archive"
            />
        </ButtonGroup>
    );
};

const FailedScenarioActions = () => {
    const [isArchiveAlertOpen, setIsArchiveAlertOpen] = useState(false);
    return (
        <>
            <AdminOnlyTooltip>
                <ButtonGroup>
                    <DuplicateScenarioAction />
                    <Popover
                        position={Position.BOTTOM_RIGHT}
                        content={
                            <Menu>
                                <MenuItem
                                    icon={IconNames.ARCHIVE}
                                    text="Archive scenario"
                                    onClick={() => {
                                        setIsArchiveAlertOpen(true);
                                    }}
                                />
                            </Menu>
                        }
                    >
                        <Button icon={IconNames.CARET_DOWN} />
                    </Popover>
                    <ArchiveScenarioAlert
                        isOpen={isArchiveAlertOpen}
                        setIsOpen={setIsArchiveAlertOpen}
                        action="archive"
                    />
                </ButtonGroup>
            </AdminOnlyTooltip>
        </>
    );
};

const PublishedScenarioActions = () => {
    const { role } = useUserDataContext();
    const [isArchiveAlertOpen, setIsArchiveAlertOpen] = useState(false);

    return (
        <>
            <ArchiveScenarioAlert
                isOpen={isArchiveAlertOpen}
                setIsOpen={setIsArchiveAlertOpen}
                action="archive"
            />
            <AdminOnlyTooltip>
                <Button
                    icon={IconNames.ARCHIVE}
                    text="Archive scenario"
                    onClick={() => setIsArchiveAlertOpen(true)}
                    disabled={role !== ADMIN_TYPE}
                />
            </AdminOnlyTooltip>
        </>
    );
};

const ArchivedScenarioActions = () => {
    const { role } = useUserDataContext();
    const isNotAdmin = role !== ADMIN_TYPE;

    const [isUnarchiveAlertOpen, setIsUnarchiveAlertOpen] = useState(false);

    return (
        <>
            <ArchiveScenarioAlert
                isOpen={isUnarchiveAlertOpen}
                setIsOpen={setIsUnarchiveAlertOpen}
                action="unarchive"
            />
            <AdminOnlyTooltip>
                <ButtonGroup>
                    <Button
                        icon={IconNames.UNARCHIVE}
                        text="Unarchive scenario"
                        onClick={() => setIsUnarchiveAlertOpen(true)}
                        disabled={isNotAdmin}
                    />
                </ButtonGroup>
            </AdminOnlyTooltip>
        </>
    );
};

export default ScenarioActions;
